// ============================================
// DÉLAIS SLA — onglet des réglages
// ============================================
// Lit et enregistre les lignes `SlaConfig` (GET /admin/sla, PUT /admin/sla/:id).
// La base stocke des minutes ; l'écran affiche des heures. L'enregistrement est
// réservé aux administrateurs côté serveur : un refus s'affiche tel quel.

import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Check, Loader2, Save } from 'lucide-react';
import { AdminApi, type SlaConfig } from '../../services/api';
import { Badge, Button } from '../../shared/ui';
import { PRIORITY_LABELS, ISSUE_TYPE_LABELS } from '../../shared/labels';
import { getPriorityColor } from '../../shared/format';
import type { IssueType, TicketPriority } from '../../types';

const ORDRE_PRIORITE = ['URGENT', 'HIGH', 'MEDIUM', 'LOW'];

/** Minutes en heures lisibles (au plus deux décimales). */
export const minutesEnHeures = (minutes: number): string => String(Math.round((minutes / 60) * 100) / 100);

/** Heures saisies en minutes entières, ou null si la saisie n'est pas un délai valable. */
export function heuresEnMinutes(saisie: string): number | null {
  const n = Number(saisie.replace(',', '.'));
  if (!Number.isFinite(n) || n <= 0) return null;
  const minutes = Math.round(n * 60);
  return minutes > 0 ? minutes : null;
}

interface Saisie { premiere: string; resolution: string }

const SlaSettingsTab: React.FC = () => {
  const [configs, setConfigs] = useState<SlaConfig[]>([]);
  const [saisies, setSaisies] = useState<Record<string, Saisie>>({});
  const [chargement, setChargement] = useState(true);
  const [enregistrement, setEnregistrement] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [confirme, setConfirme] = useState<string | null>(null);

  const appliquer = (liste: SlaConfig[]) => {
    setConfigs(liste);
    setSaisies(Object.fromEntries(liste.map(c => [c.id, {
      premiere: minutesEnHeures(c.firstResponseTime),
      resolution: minutesEnHeures(c.resolutionTime),
    }])));
  };

  useEffect(() => {
    let vivant = true;
    AdminApi.getSlaConfigs()
      .then(liste => { if (vivant) appliquer(liste); })
      .catch(e => { if (vivant) setErreur(e instanceof Error ? e.message : 'Chargement impossible'); })
      .finally(() => { if (vivant) setChargement(false); });
    return () => { vivant = false; };
  }, []);

  const triees = useMemo(() => [...configs].sort((a, b) =>
    (ORDRE_PRIORITE.indexOf(a.priority) - ORDRE_PRIORITE.indexOf(b.priority))
    || (a.issueType === null ? -1 : b.issueType === null ? 1 : a.issueType.localeCompare(b.issueType))
  ), [configs]);

  const modifiees = triees.filter(c => {
    const s = saisies[c.id];
    if (!s) return false;
    return heuresEnMinutes(s.premiere) !== c.firstResponseTime || heuresEnMinutes(s.resolution) !== c.resolutionTime;
  });
  const invalides = modifiees.filter(c => heuresEnMinutes(saisies[c.id].premiere) === null || heuresEnMinutes(saisies[c.id].resolution) === null);

  async function enregistrer() {
    if (modifiees.length === 0 || invalides.length > 0) return;
    setEnregistrement(true);
    setErreur(null);
    setConfirme(null);
    const resultats = await Promise.allSettled(modifiees.map(c => AdminApi.updateSlaConfig(c.id, {
      firstResponseTime: heuresEnMinutes(saisies[c.id].premiere)!,
      resolutionTime: heuresEnMinutes(saisies[c.id].resolution)!,
    })));
    const echecs = resultats.filter((r): r is PromiseRejectedResult => r.status === 'rejected');
    try {
      appliquer(await AdminApi.getSlaConfigs());
    } catch {
      /* la relecture a échoué : les valeurs saisies restent affichées */
    }
    if (echecs.length > 0) {
      const motif = echecs[0].reason instanceof Error ? echecs[0].reason.message : 'erreur inconnue';
      setErreur(`${resultats.length - echecs.length} délai(s) enregistré(s), ${echecs.length} échec(s) : ${motif}`);
    } else {
      setConfirme(`${resultats.length} délai(s) enregistré(s)`);
    }
    setEnregistrement(false);
  }

  if (chargement) {
    return (
      <div className="flex items-center justify-center py-12 text-graphite">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Chargement des délais SLA…
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Délais SLA</h2>
        <p className="text-sm text-graphite mt-1">
          Délais appliqués aux nouveaux dossiers selon leur priorité, en heures. Modification réservée aux administrateurs.
        </p>
      </div>

      {erreur && (
        <div role="alert" className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{erreur}</span>
        </div>
      )}

      {triees.length === 0 && !erreur ? (
        <p className="text-sm text-graphite">Aucun délai SLA n'est configuré en base.</p>
      ) : (
        <div className="space-y-3">
          {triees.map(c => {
            const s = saisies[c.id] ?? { premiere: '', resolution: '' };
            const libelle = PRIORITY_LABELS[c.priority as TicketPriority] ?? c.priority;
            const champ = (cle: keyof Saisie, titre: string) => {
              const invalide = heuresEnMinutes(s[cle]) === null;
              return (
                <label className="block">
                  <span className="text-xs text-graphite">{titre}</span>
                  <input
                    type="number" min="0.25" step="0.25" inputMode="decimal"
                    value={s[cle]}
                    onChange={e => setSaisies(prev => ({ ...prev, [c.id]: { ...s, [cle]: e.target.value } }))}
                    aria-invalid={invalide}
                    className={`mt-1 block w-full rounded-lg border bg-white py-2.5 px-3.5 text-base sm:text-sm text-encre focus:border-sapin-600 focus:ring-2 focus:ring-sapin-600/20 focus:outline-none ${invalide ? 'border-red-400' : 'border-trait'}`}
                  />
                </label>
              );
            };
            return (
              <div key={c.id} className="flex flex-col sm:flex-row sm:items-center gap-4 p-4 bg-brume rounded-lg">
                <div className="sm:w-48 flex items-center gap-2 flex-wrap">
                  <Badge className={getPriorityColor(c.priority as TicketPriority)}>{libelle}</Badge>
                  <span className="text-xs text-graphite">
                    {c.issueType ? ISSUE_TYPE_LABELS[c.issueType as IssueType] ?? c.issueType : 'Tous types'}
                  </span>
                </div>
                <div className="flex-1 grid grid-cols-2 gap-4">
                  {champ('premiere', 'Première réponse (h)')}
                  {champ('resolution', 'Résolution (h)')}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {triees.length > 0 && (
        <div className="flex items-center gap-3 flex-wrap">
          <Button variant="primary" onClick={enregistrer} disabled={enregistrement || modifiees.length === 0 || invalides.length > 0} className="w-full sm:w-auto min-h-[40px] lg:min-h-0">
            {enregistrement ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Enregistrer
          </Button>
          {invalides.length > 0 && <span className="text-sm text-red-700">Chaque délai doit être un nombre d'heures supérieur à zéro.</span>}
          {confirme && <span className="inline-flex items-center gap-1 text-sm text-green-700"><Check className="w-4 h-4" /> {confirme}</span>}
        </div>
      )}
    </div>
  );
};

export default SlaSettingsTab;
